import { useEffect, useState } from "react";

import { Home, HomeAnalyticsEvent } from "@/constants/analytics/Analytics";
import { HomeTabSubDeepLinks } from "@/constants/Home";
import { TabPath } from "@/constants/Navigation";
import { DeepLinkParams } from "@/constants/Params";
import { useLocalSearchParams } from "@/crossPlatform/expo-router";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useEncodedNavigation from "@/hooks/useEncodedNavigation";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import { useToast } from "@/hooks/useToast";
import useAppMonitor from "@/services/AppMonitor";
import { isWeb } from "@/utils/platform.util";

interface UseTaskSubDeepLink {
  handleUserChange: (userId: string) => void;
  onOpenTask: ({
    isDeepLink,
    item,
  }: {
    isDeepLink?: boolean;
    item: TaskItem;
  }) => void;
  selectedUserId: string;
  tasks?: Record<string, TaskItem[]>;
}

const useTaskSubDeepLink = ({
  handleUserChange,
  onOpenTask,
  selectedUserId,
  tasks,
}: UseTaskSubDeepLink) => {
  const { captureException } = useAppMonitor();
  const { showToast } = useToast();
  const { [DeepLinkParams.TaskId]: taskId } = useEncodedSearchParams<{
    [DeepLinkParams.TaskId]?: string;
  }>();
  const { dismissWithEncoding } = useEncodedNavigation();
  const { selectedUserId: selectedUserIdFromParams } = useLocalSearchParams<{
    selectedUserId?: string;
  }>();
  const [isSelectedUserIdInitialized, setIsSelectedUserIdInitialized] =
    useState(false);

  const flattenedTasks = tasks ? Object.values(tasks).flat() : [];

  useEffect(() => {
    if (
      !selectedUserIdFromParams ||
      isSelectedUserIdInitialized ||
      !selectedUserId
    )
      return;

    // set the selected user id on web, because web calls replaceWithEncoding on dismiss
    // mobile does not need to set it again because it calls router.dismiss() on dismiss,
    // which preserves the handleUserChange() changes in onOpen
    if (isWeb() && selectedUserIdFromParams !== selectedUserId) {
      handleUserChange(selectedUserIdFromParams);
    }
    setIsSelectedUserIdInitialized(true);
  }, [
    handleUserChange,
    isSelectedUserIdInitialized,
    selectedUserId,
    selectedUserIdFromParams,
  ]);

  const onOpen = () => {
    const task = flattenedTasks.find(
      (t: TaskItem) => t.taskExternalId === taskId,
    );

    if (!task) {
      captureException(
        new Error(
          `Assessment task deep link with taskId ${taskId} failed to load`,
        ),
      );
      if (isWeb()) {
        dismissWithEncoding({
          pathname: TabPath.Home,
        });
      }
      showToast({
        text: "Couldn’t load this assessment task",
        variant: "error",
      });
      return;
    }

    if (selectedUserId !== task.patientId) {
      handleUserChange(task.patientId);
    }

    onOpenTask({ isDeepLink: true, item: task });
  };

  useSubDeepLink({
    analyticsEvent: HomeAnalyticsEvent.TaskOpenedFromDeepLink,
    analyticsProps: {
      source: Home,
      taskId: taskId ?? "",
    },
    onOpen,
    shouldHandle: Boolean(taskId && flattenedTasks.length > 0),
    targetValue: HomeTabSubDeepLinks.Task,
  });
};

export default useTaskSubDeepLink;
